import React, { useState, useRef, useEffect } from "react";
import {
  useFrameworkData,
  FrameworkData,
  calculateDimensionScore,
  Dimension, // Assuming Dimension type is exported from useFrameworkData
} from "./useFrameworkData";
import {
  Search,
  ArrowLeftRight,
  SplitSquareHorizontal,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import { Edit } from "lucide-react";
import { Select, Tag, Input, Tooltip, Button, Progress } from "@multiagentbook/ui";
import FrameworkCard from "./FrameworkCard";
import FrameworkDetail from "./FrameworkDetail";
import DimensionSelectorModal from "./DimensionSelectorModal"; // Import the new modal component

// Define a type for the dimension structure used in the modal
interface DimensionOption {
  slug: string;
  name: string;
}

const FrameworksDashboard: React.FC = () => {
  const { frameworks, loading } = useFrameworkData();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "detail" | "compare">(
    "grid"
  );
  const [selectedFramework, setSelectedFramework] =
    useState<FrameworkData | null>(null);
  const [compareFrameworks, setCompareFrameworks] = useState<{
    left: FrameworkData | null;
    right: FrameworkData | null;
  }>({
    left: null,
    right: null,
  });
  const [sortBy, setSortBy] = useState<string>("avgScore");

  // State for dimension selection
  const [allDimensions, setAllDimensions] = useState<DimensionOption[]>([]);
  const [selectedDimensionSlugs, setSelectedDimensionSlugs] = useState<
    string[]
  >([]);
  const [isDimensionModalVisible, setIsDimensionModalVisible] = useState(false);

  // State to track which dimension cards are expanded in compare view
  const [expandedDimensions, setExpandedDimensions] = useState<{
    [key: string]: boolean;
  }>({});

  // Toggle the expanded state of a dimension
  const toggleDimension = (dimensionSlug: string) => {
    setExpandedDimensions((prev) => ({
      ...prev,
      [dimensionSlug]: !prev[dimensionSlug],
    }));
  };

  // Check URL parameters when component mounts or when frameworks load
  useEffect(() => {
    if (frameworks.length > 0 && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get("view");
      const frameworkParam = urlParams.get("framework");
      const leftParam = urlParams.get("left");
      const rightParam = urlParams.get("right");

      // Handle comparison view URL parameters
      if (viewParam === "compare" && (leftParam || rightParam)) {
        // Find the left framework
        let leftFramework: FrameworkData | null = null;
        if (leftParam) {
          leftFramework =
            frameworks.find(
              (f) =>
                f.framework_name.toLowerCase() === leftParam.toLowerCase() ||
                leftParam.toLowerCase() ===
                  f.framework_name.toLowerCase().replace(/\s+/g, "-") ||
                leftParam.toLowerCase() ===
                  f.framework_name.toLowerCase().replace(/\s+/g, "")
            ) || null;
        }

        // Find the right framework
        let rightFramework: FrameworkData | null = null;
        if (rightParam) {
          rightFramework =
            frameworks.find(
              (f) =>
                f.framework_name.toLowerCase() === rightParam.toLowerCase() ||
                rightParam.toLowerCase() ===
                  f.framework_name.toLowerCase().replace(/\s+/g, "-") ||
                rightParam.toLowerCase() ===
                  f.framework_name.toLowerCase().replace(/\s+/g, "")
            ) || null;
        }

        // If we found at least one framework to compare
        if (leftFramework || rightFramework) {
          // If only one framework was found, use the first available framework as the other one
          if (leftFramework && !rightFramework) {
            rightFramework =
              frameworks.find((f) => f !== leftFramework) || null;
          } else if (!leftFramework && rightFramework) {
            leftFramework =
              frameworks.find((f) => f !== rightFramework) || null;
          }

          // Set the compare frameworks and view mode
          setCompareFrameworks({
            left: leftFramework,
            right: rightFramework,
          });
          setViewMode("compare");
        }
      }
      // Handle single framework detail view
      else if (frameworkParam) {
        // Find the framework by ID in the URL
        const framework = frameworks.find(
          (f) =>
            f.framework_name.toLowerCase() === frameworkParam.toLowerCase() ||
            frameworkParam.toLowerCase() ===
              f.framework_name.toLowerCase().replace(/\s+/g, "-")
        );

        if (framework) {
          setSelectedFramework(framework);
          setViewMode("detail");
        }
      }
    }
  }, [frameworks, loading]); // Removed selectedDimensionSlugs dependency here

  // Extract dimensions and initialize selected dimensions when frameworks load
  useEffect(() => {
    if (frameworks.length > 0 && !loading) {
      const firstFramework = frameworks[0];
      if (firstFramework && firstFramework.dimensions) {
        const dimensions = firstFramework.dimensions.map((dim) => ({
          slug: dim.dimension_slug,
          name: dim.name,
        }));
        setAllDimensions(dimensions);
        // If selectedDimensionSlugs is empty or doesn't match, reset to all
        if (
          selectedDimensionSlugs.length === 0 ||
          selectedDimensionSlugs.length !== dimensions.length
        ) {
          setSelectedDimensionSlugs(dimensions.map((d) => d.slug));
        }
      }
    }
  }, [frameworks, loading]);

  // Listen for browser navigation events (back/forward buttons)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const urlParams = new URLSearchParams(window.location.search);
      const frameworkParam = urlParams.get("framework");

      if (frameworkParam && frameworks.length > 0) {
        // Find the framework by ID in the URL after navigation
        const framework = frameworks.find(
          (f) =>
            f.framework_name.toLowerCase() === frameworkParam.toLowerCase() ||
            frameworkParam.toLowerCase() ===
              f.framework_name.toLowerCase().replace(/\s+/g, "-")
        );

        if (framework) {
          setSelectedFramework(framework);
          setViewMode("detail");
        } else {
          // If no matching framework is found, go back to grid view
          setViewMode("grid");
        }
      } else {
        // If no framework parameter, go back to grid view
        setViewMode("grid");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [frameworks]);

  if (loading)
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <div className="animate-pulse text-secondary">
          Loading frameworks...
        </div>
      </div>
    );

  // Filter frameworks based on search (title and description)
  const filteredData = frameworks.filter((framework) => {
    if (!search) return true;

    const searchLower = search.toLowerCase();
    const nameMatch = framework.framework_name
      .toLowerCase()
      .includes(searchLower);
    const summaryMatch = framework.framework_summary
      .toLowerCase()
      .includes(searchLower);

    return nameMatch || summaryMatch;
  });

  // Helper function to calculate average score based on selected dimensions
  const calculateFilteredAverageScore = (
    framework: FrameworkData,
    selectedSlugs: string[]
  ): number => {
    if (selectedSlugs.length === 0) return 0; // Avoid division by zero

    const relevantDimensions = framework.dimensions.filter((dim) =>
      selectedSlugs.includes(dim.dimension_slug)
    );

    if (relevantDimensions.length === 0) return 0; // No selected dimensions found for this framework

    const totalScore = relevantDimensions.reduce(
      (sum, dim) => sum + calculateDimensionScore(dim),
      0
    );
    return totalScore / relevantDimensions.length;
  };

  // Sort frameworks by selected criteria, considering selected dimensions for avgScore
  const sortedData = [...filteredData].sort((a, b) => {
    if (sortBy === "avgScore") {
      // Calculate average score based on selected dimensions for sorting
      const aAvgScore = calculateFilteredAverageScore(
        a,
        selectedDimensionSlugs
      );
      const bAvgScore = calculateFilteredAverageScore(
        b,
        selectedDimensionSlugs
      );
      return bAvgScore - aAvgScore;
    } else {
      // Sort by a specific dimension
      const aDim = a.dimensions.find((d) => d.dimension_slug === sortBy);
      const bDim = b.dimensions.find((d) => d.dimension_slug === sortBy);
      const aScore = aDim ? calculateDimensionScore(aDim) : 0;
      const bScore = bDim ? calculateDimensionScore(bDim) : 0;
      return bScore - aScore;
    }
  });

  // Handle framework selection with URL update
  const handleFrameworkClick = (framework: FrameworkData) => {
    // Create a URL-friendly slug from the framework name
    const frameworkSlug = framework.framework_name
      .toLowerCase()
      .replace(/\s+/g, "-");

    // Update the URL without reloading the page
    const url = new URL(window.location.href);
    url.searchParams.set("framework", frameworkSlug);
    window.history.pushState({ framework: frameworkSlug }, "", url.toString());

    setSelectedFramework(framework);
    setViewMode("detail");
  };

  // Handle compare button click
  const handleCompareClick = () => {
    // Choose the first two frameworks by default if none are selected
    if (!compareFrameworks.left && !compareFrameworks.right) {
      const left = sortedData[0] || null;
      const right = sortedData.length > 1 ? sortedData[1] : left;
      setCompareFrameworks({ left, right });
    }

    // Update URL to reflect we're in compare mode
    const url = new URL(window.location.href);
    url.searchParams.delete("framework"); // Remove framework parameter
    url.searchParams.set("view", "compare");

    if (compareFrameworks.left && compareFrameworks.right) {
      // Use the new left/right parameter format instead of "compare"
      const leftSlug = compareFrameworks.left.framework_name
        .toLowerCase()
        .replace(/\s+/g, "-");
      const rightSlug = compareFrameworks.right.framework_name
        .toLowerCase()
        .replace(/\s+/g, "-");

      url.searchParams.set("left", leftSlug);
      url.searchParams.set("right", rightSlug);
    }

    window.history.pushState({ view: "compare" }, "", url.toString());
    setViewMode("compare");
  };

  // Handle exit from detail or compare back to grid
  const handleBack = () => {
    // Update URL by removing parameters
    const url = new URL(window.location.href);
    url.searchParams.delete("framework");
    url.searchParams.delete("view");
    url.searchParams.delete("left");
    url.searchParams.delete("right");
    url.searchParams.delete("compare"); // Remove legacy parameter if present
    window.history.pushState({}, "", url.toString());

    setViewMode("grid");
  };

  // Swap left and right frameworks in compare mode
  const handleSwapFrameworks = () => {
    const newCompare = {
      left: compareFrameworks.right,
      right: compareFrameworks.left,
    };

    setCompareFrameworks(newCompare);

    // Update URL to reflect the swap
    if (newCompare.left && newCompare.right) {
      const url = new URL(window.location.href);
      const leftSlug = newCompare.left.framework_name
        .toLowerCase()
        .replace(/\s+/g, "-");
      const rightSlug = newCompare.right.framework_name
        .toLowerCase()
        .replace(/\s+/g, "-");

      // Use the left/right parameter format
      url.searchParams.set("left", leftSlug);
      url.searchParams.set("right", rightSlug);

      window.history.replaceState({ view: "compare" }, "", url.toString());
    }
  };

  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 8) return "#52c41a"; // green
    if (score >= 6.5) return "#1890ff"; // blue
    if (score >= 5) return "#faad14"; // yellow
    return "#f5222d"; // red
  };

  // --- Dimension Modal Handlers ---
  const showDimensionModal = () => {
    setIsDimensionModalVisible(true);
  };

  const handleDimensionModalOk = (newSelectedSlugs: string[]) => {
    setSelectedDimensionSlugs(newSelectedSlugs);
    setIsDimensionModalVisible(false);
    // If sorting by average score, ensure the list re-sorts
    if (sortBy === "avgScore") {
      // Trigger a re-sort by slightly modifying the sort state (or just rely on re-render)
      // The sortedData calculation already uses the latest selectedDimensionSlugs
    }
  };

  const handleDimensionModalCancel = () => {
    setIsDimensionModalVisible(false);
  };
  // --- End Dimension Modal Handlers ---

  // Render controls for search and compare
  const renderControls = () => {
    if (viewMode === "detail") {
      return (
        <div className="mb-4">
          <Button
            onClick={handleBack}
            type="primary"
            icon={<ArrowLeft className="h-4 w-4 mr-2" />}
            className="mb-2 bg-accent hover:bg-accent/80"
          >
            Back to Frameworks
          </Button>
        </div>
      );
    }

    if (viewMode === "compare") {
      return (
        <div className="mb-4">
          <Button
            onClick={handleBack}
            type="primary"
            icon={<ArrowLeft className="h-4 w-4 mr-2" />}
            className="mb-4 bg-accent hover:bg-accent/80"
          >
            Back to Frameworks
          </Button>

          <div className="flex items-center gap-4 mb-4 bg-secondary/5 p-3 rounded-lg border border-secondary/10">
            <Select
              className="w-48"
              value={compareFrameworks.left?.framework_name}
              onChange={(value) => {
                const v = Array.isArray(value) ? value[0] : value;
                const framework =
                  frameworks.find((f) => f.framework_name === v) || null;
                setCompareFrameworks((prev) => ({ ...prev, left: framework }));

                // Update URL parameters
                const url = new URL(window.location.href);
                if (framework) {
                  const leftSlug = framework.framework_name
                    .toLowerCase()
                    .replace(/\s+/g, "-");
                  url.searchParams.set("left", leftSlug);
                  window.history.replaceState(
                    { view: "compare" },
                    "",
                    url.toString()
                  );
                }
              }}
              options={frameworks.map((framework) => ({
                label: framework.framework_name,
                value: framework.framework_name,
                disabled:
                  framework.framework_name ===
                  compareFrameworks.right?.framework_name,
              }))}
            />

            <Button
              type="primary"
              icon={<ArrowLeftRight className="w-4 h-4 text-primary" />}
              onClick={handleSwapFrameworks}
            />

            <Select
              className="w-48"
              value={compareFrameworks.right?.framework_name}
              onChange={(value) => {
                const v = Array.isArray(value) ? value[0] : value;
                const framework =
                  frameworks.find((f) => f.framework_name === v) || null;
                setCompareFrameworks((prev) => ({ ...prev, right: framework }));

                // Update URL parameters
                const url = new URL(window.location.href);
                if (framework) {
                  const rightSlug = framework.framework_name
                    .toLowerCase()
                    .replace(/\s+/g, "-");
                  url.searchParams.set("right", rightSlug);
                  window.history.replaceState(
                    { view: "compare" },
                    "",
                    url.toString()
                  );
                }
              }}
              options={frameworks.map((framework) => ({
                label: framework.framework_name,
                value: framework.framework_name,
                disabled:
                  framework.framework_name ===
                  compareFrameworks.left?.framework_name,
              }))}
            />
          </div>
        </div>
      );
    }

    // Default grid view controls
    const dimensionOptions = allDimensions.map((dim) => ({
      value: dim.slug,
      label: dim.name,
    }));

    const sortOptions = [
      {
        value: "avgScore",
        label: "Average Score", // Changed label
      },
      ...dimensionOptions,
    ];

    return (
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        {/* Group Search and Sort */}
        <div className="flex items-end gap-3 flex-grow sm:flex-grow-0">
          {/* Search Input with Label */}
          <div>
            <label className="text-xs text-secondary mb-1 block">Search</label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary"
                size={16}
              />

              <Input
                placeholder="Search frameworks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<Search className="h-4 w-4" />}
              />
            </div>
          </div>
          {/* Sort Select with Label and Edit Button */}
          <div>
            <label className="text-xs text-secondary mb-1 flex items-center">
              Sort by
              {/* Moved dimension count here */}
              {sortBy === "avgScore" && ( // Only show Edit for Average Score sort
                <Tooltip title="Edit dimensions for average score">
                  <Button
                    type="text"
                    size="small"
                    icon={<Edit className="h-3 w-3" />}
                    onClick={showDimensionModal}
                    className="ml-1 px-2 h-auto leading-none text-accent hover:text-accent/80"
                  >
                    <span className="text-xs text-accent">
                      {" "}
                      Dimension{" "}
                      {sortBy === "avgScore" && (
                        <span className="text-xs text-secondary ml-1">
                          ({selectedDimensionSlugs.length}/
                          {allDimensions.length} dims)
                        </span>
                      )}
                    </span>
                  </Button>
                </Tooltip>
              )}
            </label>

            <Select
              value={sortBy}
              onChange={(value) => {
                const v = Array.isArray(value) ? value[0] : value;
                setSortBy(v);
              }}
              options={sortOptions}
              style={{ width: 220 }}
              placeholder="Select criteria..."
            >
              {/* Options are now passed via the options prop */}
            </Select>
          </div>
        </div>
        <Button
          type="primary"
          icon={<SplitSquareHorizontal className="w-4 h-4 mr-2" />}
          onClick={handleCompareClick}
          className="bg-accent hover:bg-accent/80" // Removed self-end as items-end on parent handles alignment
        >
          Compare
        </Button>
      </div>
    );
  };

  // Render compare view with bars fanning out from center
  const renderCompareView = () => {
    if (!compareFrameworks.left || !compareFrameworks.right) return null;

    const leftFramework = compareFrameworks.left;
    const rightFramework = compareFrameworks.right;

    // Get all dimensions from both frameworks
    const allCompareDimensions = Array.from(
      new Set([
        ...leftFramework.dimensions.map((d) => d.dimension_slug),
        ...rightFramework.dimensions.map((d) => d.dimension_slug),
      ])
    ).map((slug) => {
      const leftDim = leftFramework.dimensions.find(
        (d) => d.dimension_slug === slug
      );
      const rightDim = rightFramework.dimensions.find(
        (d) => d.dimension_slug === slug
      );

      return {
        slug,
        name: leftDim?.name || rightDim?.name || "",
        leftScore: leftDim ? calculateDimensionScore(leftDim) : 0,
        leftJustification: leftDim?.dimension_justification || "",
        rightScore: rightDim ? calculateDimensionScore(rightDim) : 0,
        rightJustification: rightDim?.dimension_justification || "",
      };
    });

    // Sort dimensions by average score (highest first)
    const sortedCompareDimensions = allCompareDimensions.sort((a, b) => {
      const aAvg = (a.leftScore + a.rightScore) / 2;
      const bAvg = (b.leftScore + b.rightScore) / 2;
      return bAvg - aAvg;
    });

    // Calculate overall scores based on *selected* dimensions for display
    const leftOverallScore = calculateFilteredAverageScore(
      leftFramework,
      selectedDimensionSlugs
    );
    const rightOverallScore = calculateFilteredAverageScore(
      rightFramework,
      selectedDimensionSlugs
    );

    return (
      <div className="mb-4">
        {/* Header with framework names and overall scores - now sticky */}
        <div className="flex border-b border-secondary/20 pb-4 mb-4 sticky top-0 z-10 bg-primary bg-opacity-50 backdrop-blur shaddow-sm">
          <div className="w-1/2 px-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 overflow-hidden">
                <img
                  src={leftFramework.imageUrl}
                  alt={leftFramework.framework_name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    // If image fails to load, show initials
                    (e.target as HTMLImageElement).style.display = "none";
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      parent.innerHTML = leftFramework.framework_name
                        .split(/\s+/)
                        .map((word) => word[0])
                        .join("")
                        .toUpperCase();
                    }
                  }}
                />
              </div>
              <h3 className="font-bold text-xl text-primary">
                {leftFramework.framework_name}
              </h3>
              <Tooltip
                title={`Based on ${selectedDimensionSlugs.length}/${allDimensions.length} selected dimensions`}
              >
                <Tag
                  color={getScoreColor(leftOverallScore)}
                  className="px-3 py-1 text-base font-semibold cursor-help"
                >
                  Overall Score: {leftOverallScore.toFixed(1)}
                </Tag>
              </Tooltip>
            </div>
          </div>
          <div className="border-r border-secondary/30 mx-2"></div>
          <div className="w-1/2 px-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 overflow-hidden">
                <img
                  src={rightFramework.imageUrl}
                  alt={rightFramework.framework_name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    // If image fails to load, show initials
                    (e.target as HTMLImageElement).style.display = "none";
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      parent.innerHTML = rightFramework.framework_name
                        .split(/\s+/)
                        .map((word) => word[0])
                        .join("")
                        .toUpperCase();
                    }
                  }}
                />
              </div>
              <h3 className="font-bold text-xl text-primary">
                {rightFramework.framework_name}
              </h3>
              <Tooltip
                title={`Based on ${selectedDimensionSlugs.length}/${allDimensions.length} selected dimensions`}
              >
                <Tag
                  color={getScoreColor(rightOverallScore)}
                  className="px-3 py-1 text-base font-semibold cursor-help"
                >
                  Overall Score: {rightOverallScore.toFixed(1)}
                </Tag>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Summary section */}
        <div className="mb-4">
          <h4 className="font-semibold text-primary text-lg mb-4 text-center border-b pb-2">
            Summary
          </h4>
          <div className="flex">
            <div className="w-1/2 px-4 text-sm text-secondary">
              {leftFramework.framework_summary}
            </div>
            <div className="border-r border-secondary/30 mx-2"></div>
            <div className="w-1/2 px-4 text-sm text-secondary">
              {rightFramework.framework_summary}
            </div>
          </div>
        </div>

        {/* Dimensions comparison with bars fanning out from center */}
        <h4 className="font-semibold text-primary text-lg mb-4 text-center border-b pb-2">
          Dimensions Comparison
        </h4>

        {sortedCompareDimensions.map((dim) => {
          const leftPercent = (dim.leftScore / 10) * 100;
          const rightPercent = (dim.rightScore / 10) * 100;

          return (
            <div key={dim.slug} className="mb-6">
              <div className="text-center font-medium text-lg mb-2">
                {dim.name}
              </div>

              <div className="flex items-center mb-4">
                {/* Left score bar */}
                <div className="w-[45%] flex justify-end items-center gap-2">
                  <span
                    className="font-bold text-lg"
                    style={{ color: getScoreColor(dim.leftScore) }}
                  >
                    {dim.leftScore.toFixed(1)}
                  </span>
                  <div className="w-full max-w-[200px]">
                    <Progress
                      percent={leftPercent}
                      strokeColor={getScoreColor(dim.leftScore)}
                      showInfo={false}
                      strokeWidth={12}
                      className="flex justify-end"
                    />
                  </div>
                </div>

                {/* Center divider */}
                <div className="w-[10%] flex justify-center">
                  <div className="border-r border-secondary/50 h-8"></div>
                </div>

                {/* Right score bar */}
                <div className="w-[45%] flex justify-start items-center gap-2">
                  <div className="w-full max-w-[200px]">
                    <Progress
                      percent={rightPercent}
                      strokeColor={getScoreColor(dim.rightScore)}
                      showInfo={false}
                      strokeWidth={12}
                      className="flex justify-start"
                    />
                  </div>
                  <span
                    className="font-bold text-lg"
                    style={{ color: getScoreColor(dim.rightScore) }}
                  >
                    {dim.rightScore.toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="flex px-4">
                <div className="w-[45%] text-xs text-secondary p-2 border border-secondary/20 rounded-lg">
                  {dim.leftJustification || "No data available"}
                </div>
                <div className="w-[10%]"></div>
                <div className="w-[45%] text-xs text-secondary p-2 border border-secondary/20 rounded-lg">
                  {dim.rightJustification || "No data available"}
                </div>
              </div>

              {/* Evidence and Questions Toggle Buttons */}
              <div className="flex px-4 mt-2">
                <div className="w-[45%]">
                  {(() => {
                    const dimension = leftFramework?.dimensions?.find(
                      (d) => d.dimension_slug === dim.slug
                    );
                    return dimension?.questions &&
                      dimension.questions.length > 0 ? (
                      <button
                        onClick={() => toggleDimension(dim.slug + "-left")}
                        className="flex items-center text-accent hover:text-primary transition-colors gap-1 text-xs font-medium mt-1 opacity-70 hover:opacity-100"
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-300 ${
                            expandedDimensions[dim.slug + "-left"]
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                        <span>Evidence & Questions</span>
                      </button>
                    ) : null;
                  })()}
                </div>
                <div className="w-[10%]"></div>
                <div className="w-[45%]">
                  {(() => {
                    const dimension = rightFramework?.dimensions?.find(
                      (d) => d.dimension_slug === dim.slug
                    );
                    return dimension?.questions &&
                      dimension.questions.length > 0 ? (
                      <button
                        onClick={() => toggleDimension(dim.slug + "-right")}
                        className="flex items-center text-accent hover:text-primary transition-colors gap-1 text-xs font-medium mt-1 opacity-70 hover:opacity-100"
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-300 ${
                            expandedDimensions[dim.slug + "-right"]
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                        <span>Evidence & Questions</span>
                      </button>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Collapsible Evidence and Questions Sections */}
              <div className="flex px-4">
                {/* Left Framework Questions and Evidence */}
                <div className="w-[45%]">
                  {(() => {
                    const leftDim = leftFramework.dimensions.find(
                      (d) => d.dimension_slug === dim.slug
                    );
                    return leftDim &&
                      leftDim.questions &&
                      leftDim.questions.length > 0 ? (
                      <div
                        className={`mt-2 overflow-hidden transition-all duration-300 ease-in-out ${
                          expandedDimensions[dim.slug + "-left"]
                            ? "max-h-[500px] opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <h5 className="font-medium text-xs mb-2">Questions</h5>
                        <div className="space-y-2">
                          {leftDim.questions.map((question) => (
                            <div
                              key={question.question_slug}
                              className="pl-2 border-l-2 border-secondary/20"
                            >
                              <div className="flex justify-between mb-1">
                                <span className="text-xs">
                                  {question.question}
                                </span>
                                <Tag
                                  color={getScoreColor(question.score)}
                                  className="text-xs"
                                >
                                  {question.score.toFixed(1)}
                                </Tag>
                              </div>

                              {question.evidence &&
                                question.evidence.length > 0 && (
                                  <div className="text-xs text-secondary bg-secondary/5 p-2 rounded">
                                    <span className="font-medium">
                                      Evidence:{" "}
                                    </span>
                                    {question.evidence[0].excerpt}
                                    {question.evidence[0].source_url && (
                                      <a
                                        href={question.evidence[0].source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-1 text-accent hover:underline"
                                      >
                                        [source]
                                      </a>
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>

                <div className="w-[10%]"></div>

                {/* Right Framework Questions and Evidence */}
                <div className="w-[45%]">
                  {(() => {
                    const rightDim = rightFramework.dimensions.find(
                      (d) => d.dimension_slug === dim.slug
                    );
                    return rightDim &&
                      rightDim.questions &&
                      rightDim.questions.length > 0 ? (
                      <div
                        className={`mt-2 overflow-hidden transition-all duration-300 ease-in-out ${
                          expandedDimensions[dim.slug + "-right"]
                            ? "max-h-[500px] opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <h5 className="font-medium text-xs mb-2">Questions</h5>
                        <div className="space-y-2">
                          {rightDim.questions.map((question) => (
                            <div
                              key={question.question_slug}
                              className="pl-2 border-l-2 border-secondary/20"
                            >
                              <div className="flex justify-between mb-1">
                                <span className="text-xs">
                                  {question.question}
                                </span>
                                <Tag
                                  color={getScoreColor(question.score)}
                                  className="text-xs"
                                >
                                  {question.score.toFixed(1)}
                                </Tag>
                              </div>

                              {question.evidence &&
                                question.evidence.length > 0 && (
                                  <div className="text-xs text-secondary bg-secondary/5 p-2 rounded">
                                    <span className="font-medium">
                                      Evidence:{" "}
                                    </span>
                                    {question.evidence[0].excerpt}
                                    {question.evidence[0].source_url && (
                                      <a
                                        href={question.evidence[0].source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-1 text-accent hover:underline"
                                      >
                                        [source]
                                      </a>
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render main content based on view mode
  const renderContent = () => {
    if (viewMode === "detail" && selectedFramework) {
      return <FrameworkDetail framework={selectedFramework} />;
    }

    if (
      viewMode === "compare" &&
      compareFrameworks.left &&
      compareFrameworks.right
    ) {
      return renderCompareView();
    }

    // Default grid view
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {sortedData.length === 0 ? (
          <div className="col-span-full py-8 text-center text-secondary">
            No frameworks match your search or filter criteria
          </div>
        ) : (
          sortedData.map(
            (
              framework: FrameworkData,
              index: number // Add types for framework and index
            ) => (
              <FrameworkCard
                key={framework.framework_name}
                framework={framework}
                onClick={handleFrameworkClick}
                isTopFramework={index === 0}
                index={index}
                // Pass selected dimensions to card if needed for display
                // selectedDimensionSlugs={selectedDimensionSlugs}
                // Pass calculated score based on selected dimensions
                displayScore={calculateFilteredAverageScore(
                  framework,
                  selectedDimensionSlugs
                )}
              />
            )
          )
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderControls()}
      {renderContent()}

      {/* Render the Dimension Selector Modal */}
      <DimensionSelectorModal
        isVisible={isDimensionModalVisible}
        allDimensions={allDimensions}
        selectedSlugs={selectedDimensionSlugs}
        onOk={handleDimensionModalOk}
        onCancel={handleDimensionModalCancel}
        allDimensionsFull={frameworks[0]?.dimensions?.map((dim) => ({
          slug: dim.dimension_slug,
          name: dim.name,
          questions: dim.questions || [],
        }))}
      />
    </div>
  );
};

export default FrameworksDashboard;
